import React, { useState, useCallback, useMemo } from 'react';
import orderBy from 'lodash/orderBy';

import { toast } from '@hum/common/src/modules/toast';
import { CompanyFileDocumentType, CompanyFile, FileInput } from '@hum/types';
import { useDownloadFile } from './download';
// import { useCreate } from './create';
import { analytics } from '@hum/common';
import {
  deleteFile,
  getCompanyFiles,
  uploadFile,
  useApiMutation,
  useApiQuery,
} from '@hum/api';

export type Progress = Record<string, number>;
type ActiveMap = Record<string, boolean>;

interface FileManagerConfig {
  companyId: string;
  filter?: (file: CompanyFile) => boolean;
}

/**
 * Fully featured file manager.
 * WARN: Do not delete this manager until we have another 100% working one with events and tests attached to it
 */
const useFileManager = ({
  companyId,
  filter = () => true,
}: FileManagerConfig) => {
  const { data: files, isLoading: companyFilesIsLoading } = useApiQuery(
    getCompanyFiles,
    {
      params: { companyId: companyId || '' },
    }
  );
  const [creating] = useState<ActiveMap>({});
  const [uploading] = useState<ActiveMap>({});
  const [progress] = useState<Progress>({});
  const [uploadingFiles] = useState<Record<string, File>>({});
  const [created, setCreated] = useState<any | null>(null);
  const { mutate: removeFile, isLoading: removing } = useApiMutation(
    deleteFile,
    {
      onSuccess: () => {
        toast.success('File removed successfully');
      },
    }
  );
  const { mutate: uploadLocalFile } = useApiMutation(uploadFile, {
    onSuccess: (data) => {
      toast.success('File uploaded successfully');
      setCreated(data);
    },
    onError: () => {
      toast.error('Failed to upload file.');
    },
  });
  // const { create, upload } = useCreate(companyId);
  const { download: downloadFile } = useDownloadFile(companyId);

  const filteredFiles = useMemo(
    () => orderBy((files || []).filter(filter), ['createdAt'], ['desc']),
    [files]
  );

  const createFromLocalFile = async ({
    input,
    file,
  }: {
    reference?: string;
    input: FileInput;
    file: File;
  }) => {
    const { name, type } = file;

    input.name = input.name || name;
    input.type = input.type || type;
    input.isExternal = false;

    toast.info('Uploading file...');
    await uploadLocalFile({
      documentType: input.documentType,
      name: input.name,
      fileType: input.type,
      isExternal: false,
      blob: file,
    });

    return created;
  };

  const handleFileChange = useCallback(
    (
      documentType: CompanyFileDocumentType,
      meta: Record<string, any> = {},
      onFileUpload: (file: File) => void
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);

      e.target.value = '';

      selectedFiles.forEach(async (file) => {
        try {
          await createFromLocalFile({
            input: { name: file.name, type: file.type, documentType, meta },
            file,
          });

          // @ts-ignore
          analytics.track('document-upload-success', {
            documentType,
            companyID: companyId,
          });
          onFileUpload(file);
        } catch (error: any) {
          // @ts-ignore
          analytics.track('document-upload-failed', {
            documentType,
            companyID: companyId,
          });
          toast.error(error?.message || 'Failed to save file.');
        }
      });
    },
    [companyId, createFromLocalFile]
  );

  return {
    // data
    // query,
    files: filteredFiles,
    loading: files === null && !companyFilesIsLoading,
    // raw actions
    // create,
    // upload,
    // state
    handleFileChange,
    creating,
    uploading,
    uploadingFiles,
    removing,
    progress,
    // handlers
    createFromLocalFile,
    downloadFile,
    removeFile,
    loadFiles: () => files,
  };
};

export type FileManager = ReturnType<typeof useFileManager>;

export { useFileManager };
